/**
 * ABC Brianza — Scroll reveal per il contenuto delle pagine statiche.
 *
 * Il tema (Dawn/Refresh) include già un motore di scroll-reveal nativo
 * (vedi assets/animations.js + le regole .scroll-trigger / .animate--*
 * in assets/base.css), attivo perché settings.animations_reveal_on_scroll
 * è true. Quel motore tagga automaticamente hero, griglie prodotto e
 * card — ma NON il contenuto delle pagine statiche (Info, Servizi,
 * Modelcars, Special Projects, B2B), che arriva come blocco unico
 * `.rte` con l'HTML della pagina così com'è.
 *
 * Questo script non reinventa il meccanismo: riusa le stesse classi
 * CSS del tema (stessa easing, stessa durata, stessa gestione di
 * prefers-reduced-motion già verificata in base.css) e applica un
 * proprio IntersectionObserver ai singoli blocchi (h2/h3/p/ul, righe
 * FAQ) così che ognuno entri in vista in modo indipendente man mano
 * che si scorre la pagina, invece di animare tutto il blocco pagina
 * in un colpo solo.
 *
 * Nessuna manipolazione del DOM (niente wrapper/spostamento nodi):
 * si aggiungono solo classi ad elementi già esistenti, per non
 * interferire con le regole `.rte > *:first-child` ecc. già presenti
 * nel tema.
 *
 * Estensione (pagina Chi Siamo, sistema editoriale): oltre ai blocchi
 * "piatti" h2/h3/p/ul, il body_html può ora contenere blocchi wrapper
 * (.abc-lead-block, .abc-editorial-block, .abc-team-section,
 * .abc-stats-section) e gruppi di elementi affiancati che devono
 * entrare in scena in sequenza (card team, stat-tile, badge partner),
 * non tutti insieme. Per questi gruppi si riusa .animate--slide-in
 * (non .animate--fade-in): è l'unica delle due classi del tema che
 * in base.css applica un vero animation-delay basato su
 * --animation-order (`calc(var(--animation-order) * 75ms)`) — .animate
 * --fade-in ignora quella variabile. Il gruppo FAQ preesistente resta
 * con fade-in per non alterare un comportamento già in produzione su
 * più pagine (Servizi, Modelcars, Special Projects, B2B).
 *
 * Estensione 2 (Servizi, Modelcars, Special Projects, B2B): aggiunti
 * .abc-intro-lead ai blocchi wrapper top-level, e due nuovi gruppi a
 * stagger — .abc-feature-card (Servizi, Special Projects) e
 * .abc-path-card (B2B, i due percorsi) — con lo stesso trattamento
 * slide-in di card team / stat-tile / badge partner.
 *
 * Estensione 3 (09/08, Chi Siamo — timeline storica): aggiunto il
 * wrapper .abc-timeline ai blocchi top-level e un nuovo gruppo a
 * stagger, .abc-timeline-item (dentro .abc-timeline), stesso
 * trattamento slide-in degli altri gruppi a card.
 *
 * Estensione 4 (09/08, contachilometri sulle stat-tile): questo file
 * riusa lo stesso IntersectionObserver che gestisce il reveal per
 * avviare anche il count-up numerico di .abc-stat-number quando la sua
 * .abc-stat-tile entra in viewport — stesso trigger "una volta sola",
 * nessun secondo observer. Se prefers-reduced-motion è attivo o
 * IntersectionObserver non è supportato, la funzione esce subito (vedi
 * guardia in cima al file): niente reveal E niente count-up, il valore
 * finale statico già presente nell'HTML resta l'unico stato visibile —
 * mai un flash "0" prima che il JS carichi, mai un conteggio per chi ha
 * chiesto meno motion.
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion || !('IntersectionObserver' in window)) {
    // Chi richiede meno motion, o un browser senza IntersectionObserver,
    // vede il contenuto statico e completo: nessuna classe viene aggiunta.
    return;
  }

  var REVEAL_SELECTOR = [
    '.rte > h2',
    '.rte > h3',
    '.rte > p',
    '.rte > ul',
    '.rte > ol',
    '.rte > blockquote',
    '.rte > .abc-lead-block',
    '.rte > .abc-intro-lead',
    '.rte > .abc-editorial-block',
    '.rte > .abc-timeline',
    '.rte > .abc-team-section',
    '.rte > .abc-stats-section',
    '.rte > .abc-feature-section',
    '.rte > .abc-path-section',
    '.abc-faq-item',
    '.abc-profile-card',
    '.abc-stat-tile',
    '.abc-partner-badge',
    '.abc-feature-card',
    '.abc-path-card',
    '.abc-timeline-item'
  ].join(', ');
  var PROCESSED_ATTR = 'abcMotionReveal';

  // Gruppi di elementi affiancati che devono comparire in sequenza
  // (uno stagger per figlio), invece che come blocco unico.
  var STAGGER_GROUPS = [
    { itemClass: 'abc-faq-item', parentSelector: '.abc-faq', animClass: 'animate--fade-in' },
    { itemClass: 'abc-profile-card', parentSelector: '.abc-profile-grid', animClass: 'animate--slide-in' },
    { itemClass: 'abc-stat-tile', parentSelector: '.abc-stat-grid', animClass: 'animate--slide-in' },
    { itemClass: 'abc-partner-badge', parentSelector: '.abc-partner-badges', animClass: 'animate--slide-in' },
    { itemClass: 'abc-feature-card', parentSelector: '.abc-feature-grid', animClass: 'animate--slide-in' },
    { itemClass: 'abc-path-card', parentSelector: '.abc-path-grid', animClass: 'animate--slide-in' },
    { itemClass: 'abc-timeline-item', parentSelector: '.abc-timeline', animClass: 'animate--slide-in' }
  ];

  function findStaggerGroup(el) {
    for (var i = 0; i < STAGGER_GROUPS.length; i++) {
      if (el.classList.contains(STAGGER_GROUPS[i].itemClass)) return STAGGER_GROUPS[i];
    }
    return null;
  }

  function tagElements(root) {
    var elements = root.querySelectorAll(REVEAL_SELECTOR);
    var tagged = [];

    elements.forEach(function (el) {
      if (el.dataset[PROCESSED_ATTR]) return;
      el.dataset[PROCESSED_ATTR] = 'true';

      var group = findStaggerGroup(el);

      if (group) {
        el.classList.add('scroll-trigger', group.animClass);

        var parent = el.closest(group.parentSelector);
        if (parent) {
          var siblings = Array.prototype.filter.call(parent.children, function (child) {
            return child.classList.contains(group.itemClass);
          });
          el.style.setProperty('--animation-order', siblings.indexOf(el));
        }
      } else {
        el.classList.add('scroll-trigger', 'animate--slide-in');
      }

      tagged.push(el);
    });

    return tagged;
  }

  /**
   * Contachilometri sulle stat-tile ("ABC Brianza in numeri" / "Il
   * catalogo in numeri"). Legge il valore già presente in .abc-stat-number
   * (progressive enhancement: quel testo è il valore vero, non un
   * placeholder — se il parsing fallisce o JS non parte, resta quello,
   * mai un "0" o un NaN a schermo).
   *
   * Formati gestiti: "1973" (anno, 4 cifre, nessun separatore — NON va
   * raggruppato con un punto durante il conteggio, altrimenti diventa
   * "1.973" e non torna al valore originale a fine animazione), "1.000+"
   * / "20.000+" / "30.000+" (punto delle migliaia italiano + suffisso
   * "+"), "150+" (sotto le migliaia, nessun punto), "7" (cifra singola).
   * Tutto ciò che non è puramente numerico (es. "1/87–1/10", la scala
   * coperta su Servizi) non supera il parsing: niente animazione, il
   * valore statico resta a schermo così com'è, senza errori.
   */
  var STAT_VALUE_RE = /^(\d{1,3}(?:\.\d{3})*|\d+)(\+)?$/;

  function parseStatValue(rawText) {
    var raw = (rawText || '').trim();
    var match = raw.match(STAT_VALUE_RE);
    if (!match) return null;

    var digitsPart = match[1];
    var value = parseInt(digitsPart.replace(/\./g, ''), 10);
    if (isNaN(value)) return null;

    return {
      finalText: raw,
      value: value,
      hasPlus: !!match[2],
      // Raggruppa con il punto delle migliaia in corso d'animazione SOLO
      // se il testo originale lo usava già (es. "20.000+"): un anno come
      // "1973" non ha mai il punto e deve restare "1973" cifra per cifra.
      grouped: digitsPart.indexOf('.') !== -1
    };
  }

  function formatStatFrame(n, parsed) {
    var out = parsed.grouped ? n.toLocaleString('it-IT') : String(n);
    return parsed.hasPlus ? out + '+' : out;
  }

  function animateCountUp(el, parsed) {
    if (!('requestAnimationFrame' in window)) {
      el.textContent = parsed.finalText;
      return;
    }

    var duration = 1300; // ms — breve ed elegante, come da brief (1.2-1.5s)
    var start = null;

    function step(timestamp) {
      if (start === null) start = timestamp;
      var progress = Math.min((timestamp - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      var current = Math.round(parsed.value * eased);

      el.textContent = formatStatFrame(current, parsed);

      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        // Ultimo frame: testo originale byte per byte, non ricostruito —
        // garanzia che il conteggio atterri esattamente sul valore reale.
        el.textContent = parsed.finalText;
      }
    }

    requestAnimationFrame(step);
  }

  function maybeAnimateStat(el) {
    if (!el.classList || !el.classList.contains('abc-stat-tile')) return;

    var numberEl = el.querySelector('.abc-stat-number');
    if (!numberEl || numberEl.dataset.abcCounted) return;
    numberEl.dataset.abcCounted = 'true';

    var parsed = parseStatValue(numberEl.textContent);
    if (!parsed) return; // non numerico (es. "1/87–1/10"): resta statico

    animateCountUp(numberEl, parsed);
  }

  function observeElements(elements) {
    if (!elements.length) return;

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.remove('scroll-trigger--offscreen');
            obs.unobserve(entry.target);
            maybeAnimateStat(entry.target);
          } else {
            entry.target.classList.add('scroll-trigger--offscreen');
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    elements.forEach(function (el) {
      observer.observe(el);
    });
  }

  function init(root) {
    observeElements(tagElements(root || document));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Nel theme editor Shopify ricarica la sezione via AJAX: i nostri
  // attributi/classi vanno riapplicati sul nodo appena sostituito.
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  }
})();

/**
 * B2B — apertura sincronizzata dei due percorsi (.abc-path-card).
 *
 * 09/08 v5, richiesta cliente: le due card <details> di /pages/b2b
 * (Percorso 1 / Percorso 2) erano indipendenti — aprendone una,
 * l'altra restava chiusa e sembrava "vuota"/inesistente invece di una
 * seconda opzione ancora da esplorare. Ora: aprire o chiudere una
 * qualsiasi card dentro .abc-path-grid porta tutte le altre card dello
 * stesso gruppo allo stesso stato open/closed.
 *
 * IIFE separata e volutamente NON dentro la guardia
 * prefers-reduced-motion/IntersectionObserver qui sopra: quella
 * guardia riguarda il reveal-on-scroll (un'animazione), questa è pura
 * sincronizzazione di stato, senza alcuna transizione — deve
 * funzionare identica per tutti, a prescindere da prefers-reduced-
 * motion o dal supporto a IntersectionObserver.
 *
 * Ascolta il solo evento nativo 'toggle' di <details> — nessuna
 * gestione di click/tastiera reinventata: Tab/Invio/Spazio sul
 * <summary> restano gestiti dal browser esattamente come prima, su
 * entrambe le card. Il browser non riemette 'toggle' se .open viene
 * impostato allo stesso valore che aveva già, quindi propagare lo
 * stato alle altre card non genera loop: l'altra card cambia stato una
 * sola volta, la propagazione di ritorno verso la prima trova .open
 * già allineato e si ferma lì.
 *
 * Cambio di stato istantaneo (nessuna transizione aggiunta), come già
 * per lo stile [open] esistente in custom-abc-editorial.css — nessun
 * conflitto con prefers-reduced-motion perché non c'è alcuna
 * animazione da rispettare.
 */
(function () {
  'use strict';

  function syncGroup(grid) {
    var cards = Array.prototype.filter.call(grid.children, function (child) {
      return child.classList && child.classList.contains('abc-path-card');
    });
    if (cards.length < 2) return;

    cards.forEach(function (card) {
      card.addEventListener('toggle', function () {
        var isOpen = card.open;
        cards.forEach(function (other) {
          if (other !== card && other.open !== isOpen) {
            other.open = isOpen;
          }
        });
      });
    });
  }

  function init(root) {
    (root || document).querySelectorAll('.abc-path-grid').forEach(function (grid) {
      if (grid.dataset.abcPathSync) return;
      grid.dataset.abcPathSync = 'true';
      syncGroup(grid);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Nel theme editor Shopify ricarica la sezione via AJAX: i nostri
  // listener 'toggle' vanno riagganciati sui nodi <details> appena
  // sostituiti (i vecchi nodi con i loro listener spariscono col DOM
  // sostituito, non c'è leak da ripulire).
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      init(event.target);
    });
  }
})();

/*
 * FAQPage JSON-LD — GEO/AEO: le pagine con accordion FAQ (Chi Siamo,
 * Servizi, Special Projects, B2B) avevano il markup accessibile
 * <details>/<summary> ma nessuno schema strutturato — un gap comune su
 * Shopify, i motori di risposta (Google AI Overviews, ChatGPT,
 * Perplexity) leggono più affidabilmente i dati strutturati del testo
 * grezzo. Generato via JS leggendo il DOM già renderizzato (non il
 * body_html sorgente): funziona automaticamente in italiano, inglese,
 * francese e tedesco senza toccare le traduzioni, perché legge qualunque
 * lingua sia effettivamente a schermo. Un blocco <script type=
 * "application/ld+json"> per ogni gruppo .abc-faq trovato in pagina
 * (nel nostro caso sempre uno solo, ma supporta più gruppi per
 * sicurezza). Nessun impatto visivo: lo script è iniettato invisibile
 * in coda al <body>.
 */
(function () {
  'use strict';

  function textOf(el) {
    return (el && el.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function buildFaqSchema(faqGroup) {
    var items = faqGroup.querySelectorAll(':scope > .abc-faq-item');
    var entities = [];
    items.forEach(function (item) {
      var summary = item.querySelector('summary');
      var answerParts = item.querySelectorAll('p');
      var question = textOf(summary);
      var answer = Array.prototype.map.call(answerParts, textOf).filter(Boolean).join(' ');
      if (question && answer) {
        entities.push({
          '@type': 'Question',
          name: question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: answer
          }
        });
      }
    });
    if (!entities.length) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: entities
    };
  }

  function init() {
    document.querySelectorAll('.abc-faq').forEach(function (faqGroup) {
      if (faqGroup.dataset.abcFaqSchemaDone) return;
      faqGroup.dataset.abcFaqSchemaDone = 'true';
      var schema = buildFaqSchema(faqGroup);
      if (!schema) return;
      var script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.body.appendChild(script);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
